import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';

export type GbpPerfRange = 'mtd' | '7d' | '28d' | '90d' | '6m' | '12m' | '18m';

export const GBP_PERF_RANGES: Array<{ key: GbpPerfRange; label: string }> = [
  { key: 'mtd', label: 'This month' },
  { key: '7d', label: '7 days' },
  { key: '28d', label: '28 days' },
  { key: '90d', label: '90 days' },
  { key: '6m', label: '6 months' },
  { key: '12m', label: '12 months' },
  { key: '18m', label: '18 months' },
];

export interface GbpPerfDay {
  date: string;
  impressionsDesktopMaps: number;
  impressionsDesktopSearch: number;
  impressionsMobileMaps: number;
  impressionsMobileSearch: number;
  impressions: number;
  impressionsSearch: number;
  impressionsMaps: number;
  conversations: number;
  directionRequests: number;
  calls: number;
  websiteClicks: number;
  bookings: number;
  interactions: number;
}

export type GbpPerfTotals = Omit<GbpPerfDay, 'date'>;

export interface GbpPerfKeyword {
  keyword: string;
  impressions: number | null;
  threshold: number | null;
  approx: boolean;
}

export interface GbpPerformance {
  range: GbpPerfRange;
  start: string;
  end: string;
  dataThrough: string | null;
  days: GbpPerfDay[];
  totals: GbpPerfTotals;
  previousTotals: GbpPerfTotals | null;
  previousRange: { start: string; end: string } | null;
  keywords: GbpPerfKeyword[];
  /** Months the keyword list covers (first-of-month ISO dates) — always the selected range's months. */
  keywordsPeriod: { start: string; end: string } | null;
  metricsSyncedAt: string | null;
  metricsError: string | null;
  keywordsError: string | null;
  locationName: string | null;
}

interface State {
  data: GbpPerformance | null;
  loading: boolean;
  error: string | null;
  /** True when the connection is an aggregator (no Performance API). */
  unsupported: boolean;
}

/**
 * Google Business Profile performance for one connection. The edge function
 * caches Google's data server-side, so re-requests are cheap; `refresh`
 * forces a re-pull.
 */
export function useGbpPerformance(
  businessId: string | null | undefined,
  connectionId: string | null | undefined,
  range: GbpPerfRange,
  opts?: { enabled?: boolean },
) {
  const enabled = opts?.enabled !== false && !!businessId && !!connectionId;
  const [state, setState] = useState<State>({
    data: null,
    loading: enabled,
    error: null,
    unsupported: false,
  });
  const requestId = useRef(0);

  const load = useCallback(
    async (force = false) => {
      if (!enabled) return;
      const id = ++requestId.current;
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const { data, error } = await supabase.functions.invoke('google-gbp-performance', {
          body: { businessId, connectionId, range, force },
        });
        if (id !== requestId.current) return;
        if (error || data?.error) {
          const msg = (await invokeErrorMessage(error, data)) || 'Failed to load insights';
          setState({ data: null, loading: false, error: msg, unsupported: false });
          return;
        }
        if (data?.unsupported) {
          setState({ data: null, loading: false, error: null, unsupported: true });
          return;
        }
        setState({ data: data as GbpPerformance, loading: false, error: null, unsupported: false });
      } catch (err) {
        if (id !== requestId.current) return;
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load insights',
          unsupported: false,
        });
      }
    },
    [enabled, businessId, connectionId, range],
  );

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null, unsupported: false });
      return;
    }
    void load(false);
  }, [enabled, load]);

  return { ...state, refresh: () => load(true) };
}

/** Percent change, or null when there is no meaningful baseline. */
export function pctChange(current: number, previous: number | null | undefined): number | null {
  if (previous == null) return null;
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
}

const TOTAL_KEYS: (keyof GbpPerfTotals)[] = [
  'impressionsDesktopMaps',
  'impressionsDesktopSearch',
  'impressionsMobileMaps',
  'impressionsMobileSearch',
  'impressions',
  'impressionsSearch',
  'impressionsMaps',
  'conversations',
  'directionRequests',
  'calls',
  'websiteClicks',
  'bookings',
  'interactions',
];

function zeroTotals(): GbpPerfTotals {
  return {
    impressionsDesktopMaps: 0,
    impressionsDesktopSearch: 0,
    impressionsMobileMaps: 0,
    impressionsMobileSearch: 0,
    impressions: 0,
    impressionsSearch: 0,
    impressionsMaps: 0,
    conversations: 0,
    directionRequests: 0,
    calls: 0,
    websiteClicks: 0,
    bookings: 0,
    interactions: 0,
  };
}

function addTotals(left: GbpPerfTotals, right: GbpPerfTotals): GbpPerfTotals {
  const next = { ...left };
  for (const key of TOTAL_KEYS) next[key] = (Number(next[key]) || 0) + (Number(right[key]) || 0);
  return next;
}

function mergeKeywords(lists: GbpPerfKeyword[][]): GbpPerfKeyword[] {
  const map = new Map<
    string,
    { keyword: string; impressions: number; hidden: boolean; threshold: number | null; approx: boolean }
  >();
  for (const list of lists) {
    for (const keyword of list) {
      const key = keyword.keyword.trim().toLowerCase();
      const current = map.get(key) || {
        keyword: keyword.keyword,
        impressions: 0,
        hidden: false,
        threshold: null,
        approx: false,
      };
      if (keyword.impressions == null) {
        current.hidden = true;
        current.threshold = Math.max(current.threshold ?? 0, keyword.threshold ?? 15);
      } else {
        current.impressions += keyword.impressions;
        if (keyword.approx) current.approx = true;
      }
      map.set(key, current);
    }
  }
  return [...map.values()]
    .map((keyword) => ({
      keyword: keyword.keyword,
      impressions: keyword.hidden && keyword.impressions === 0 ? null : keyword.impressions,
      threshold: keyword.hidden ? keyword.threshold : null,
      approx: keyword.approx || keyword.hidden,
    }))
    .sort((a, b) => (b.impressions ?? 0) - (a.impressions ?? 0));
}

/** Sum performance payloads that share a date range (one row per location). */
export function mergeGbpPerformance(parts: GbpPerformance[]): GbpPerformance {
  const first = parts[0];
  const days = new Map<string, GbpPerfDay>();
  let totals = zeroTotals();
  let previousTotals: GbpPerfTotals | null = null;
  let dataThrough: string | null = null;

  for (const part of parts) {
    totals = addTotals(totals, part.totals);
    if (part.previousTotals) {
      previousTotals = previousTotals
        ? addTotals(previousTotals, part.previousTotals)
        : { ...part.previousTotals };
    }
    if (part.dataThrough && (!dataThrough || part.dataThrough < dataThrough)) {
      dataThrough = part.dataThrough;
    }
    for (const day of part.days) {
      const existing = days.get(day.date);
      if (!existing) {
        days.set(day.date, { ...day });
        continue;
      }
      const next = { ...existing };
      for (const key of TOTAL_KEYS) next[key] = (Number(existing[key]) || 0) + (Number(day[key]) || 0);
      days.set(day.date, next);
    }
  }

  const errors = parts.map((part) => part.metricsError).filter((message): message is string => !!message);
  const keywordErrors = parts
    .map((part) => part.keywordsError)
    .filter((message): message is string => !!message);

  return {
    range: first.range,
    start: first.start,
    end: first.end,
    dataThrough,
    days: [...days.values()].sort((a, b) => a.date.localeCompare(b.date)),
    totals,
    previousTotals,
    previousRange: first.previousRange,
    keywords: mergeKeywords(parts.map((part) => part.keywords || [])),
    keywordsPeriod: first.keywordsPeriod,
    metricsSyncedAt: first.metricsSyncedAt,
    metricsError: errors.length ? errors[0] : null,
    keywordsError: keywordErrors.length && parts.every((part) => part.keywords.length === 0) ? keywordErrors[0] : null,
    locationName: null,
  };
}

/**
 * Performance for one or more direct Google locations. Several locations are
 * summed into the same shape the single-location sheet already renders.
 */
export function useGbpPerformanceSet(
  businessId: string | null | undefined,
  connectionIds: string[],
  range: GbpPerfRange,
  opts?: { enabled?: boolean },
) {
  const idKey = connectionIds.filter(Boolean).join(',');
  const enabled = opts?.enabled !== false && !!businessId && idKey.length > 0;
  const [state, setState] = useState<State>({
    data: null,
    loading: enabled,
    error: null,
    unsupported: false,
  });
  const requestId = useRef(0);

  const load = useCallback(
    async (force = false) => {
      const ids = idKey ? idKey.split(',') : [];
      if (!businessId || ids.length === 0) return;
      const id = ++requestId.current;
      setState((current) => ({ ...current, loading: true, error: null }));
      try {
        const results = await Promise.all(
          ids.map(async (connectionId) => {
            const { data, error } = await supabase.functions.invoke('google-gbp-performance', {
              body: { businessId, connectionId, range, force },
            });
            if (error || data?.error) {
              return {
                ok: false as const,
                unsupported: false,
                message: (await invokeErrorMessage(error, data)) || 'Failed to load insights',
              };
            }
            if (data?.unsupported) return { ok: false as const, unsupported: true, message: '' };
            return { ok: true as const, unsupported: false, data: data as GbpPerformance };
          }),
        );
        if (id !== requestId.current) return;
        const usable = results.filter((result) => result.ok);
        if (usable.length === 0) {
          const unsupported = results.every((result) => result.unsupported);
          const message = results.find((result) => result.message)?.message || 'Failed to load insights';
          setState({
            data: null,
            loading: false,
            error: unsupported ? null : message,
            unsupported,
          });
          return;
        }
        const merged =
          usable.length === 1 ? usable[0].data : mergeGbpPerformance(usable.map((result) => result.data));
        setState({ data: merged, loading: false, error: null, unsupported: false });
      } catch (err) {
        if (id !== requestId.current) return;
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load insights',
          unsupported: false,
        });
      }
    },
    [businessId, idKey, range],
  );

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null, unsupported: false });
      return;
    }
    void load(false);
  }, [enabled, load]);

  return { ...state, refresh: () => load(true) };
}
